import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { supabase } from '../../lib/supabase';
import { useAuth } from '../../contexts/AuthContext';
import Icon from '../../components/AppIcon';
import SpinnerOverlay from '../../components/ui/SpinnerOverlay';
import SuccessToast from '../../components/ui/SuccessToast';

const STEPS = [
  { id: 1, label: 'Owner KYC', icon: 'User' },
  { id: 2, label: 'Vehicle Details', icon: 'Car' },
  { id: 3, label: 'Availability', icon: 'Calendar' },
  { id: 4, label: 'Documents', icon: 'FileText' },
  { id: 5, label: 'Photos', icon: 'Camera' },
  { id: 6, label: 'Pricing', icon: 'DollarSign' },
  { id: 7, label: 'Review', icon: 'CheckCircle' },
];

const ListMyCarScreen = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const editVehicleId = searchParams?.get('edit') || null;
  const isEditMode = !!editVehicleId;

  const { user } = useAuth();
  const [step, setStep] = useState(1);
  const [submitting, setSubmitting] = useState(false);
  const [loadingEdit, setLoadingEdit] = useState(isEditMode);
  const [showSuccess, setShowSuccess] = useState(false);
  const [errors, setErrors] = useState({});

  const [kyc, setKyc] = useState({ fullName: '', phone: '', idNumber: '', payoutMethod: 'wallet', termsAccepted: false });
  const [vehicleDetails, setVehicleDetails] = useState({ make: '', model: '', year: new Date()?.getFullYear(), plate: '', seats: 5, transmission: 'automatic', fuel: 'petrol', color: '', location: '', rentalMode: 'self_drive', description: '' });
  const [availability, setAvailability] = useState({ minDays: 1, maxDays: 30, minDriverAge: 23, depositRequired: true });
  const [docFiles, setDocFiles] = useState({ logbook: null, insurance: null });
  const [photos, setPhotos] = useState([]);
  const [pricing, setPricing] = useState({ baseDailyRate: '', weekendMultiplier: 1.2, chauffeurAddon: '', depositAmount: '', mileageLimit: 200, overageFee: 25 });

  // Load existing vehicle data in edit mode
  useEffect(() => {
    if (!isEditMode || !user) return;
    const loadVehicle = async () => {
      setLoadingEdit(true);
      try {
        const { data: v, error } = await supabase
          ?.from('vehicles')
          ?.select(`*, vehicle_pricing(*)`)
          ?.eq('id', editVehicleId)
          ?.eq('owner_id', user?.id)
          ?.single();

        if (error || !v) {
          navigate('/my-vehicles');
          return;
        }

        // Pre-populate form fields
        setVehicleDetails({
          make: v?.make || '',
          model: v?.model || '',
          year: v?.year || new Date()?.getFullYear(),
          plate: v?.plate || '',
          seats: v?.seats || 5,
          transmission: v?.transmission || 'automatic',
          fuel: v?.fuel || 'petrol',
          color: v?.color || '',
          location: v?.location_address || '',
          rentalMode: v?.rental_mode || 'self_drive',
          description: v?.description || '',
        });
        setAvailability({
          minDays: v?.min_rental_days || 1,
          maxDays: v?.max_rental_days || 30,
          minDriverAge: v?.min_driver_age || 23,
          depositRequired: v?.deposit_required ?? true,
        });

        const p = v?.vehicle_pricing?.[0];
        if (p) {
          setPricing({
            baseDailyRate: p?.base_daily_rate?.toString() || '',
            weekendMultiplier: p?.weekend_multiplier || 1.2,
            chauffeurAddon: p?.chauffeur_daily_addon?.toString() || '',
            depositAmount: p?.deposit_amount?.toString() || '',
            mileageLimit: p?.mileage_limit_km_per_day || 200,
            overageFee: p?.overage_fee_per_km || 25,
          });
        }

        // Pre-fill KYC from user profile
        const { data: profile } = await supabase?.from('user_profiles')?.select('full_name, phone')?.eq('id', user?.id)?.single();
        if (profile) {
          setKyc(k => ({ ...k, fullName: profile?.full_name || '', phone: profile?.phone || '', termsAccepted: true }));
        }
      } catch (e) {
        console.error('Failed to load vehicle for edit:', e);
        navigate('/my-vehicles');
      } finally {
        setLoadingEdit(false);
      }
    };
    loadVehicle();
  }, [editVehicleId, user, isEditMode]);

  const validateStep = () => {
    const errs = {};
    if (step === 1) {
      if (!kyc?.fullName?.trim()) errs.fullName = 'Required';
      if (!kyc?.phone?.trim()) errs.phone = 'Required';
      if (!kyc?.termsAccepted) errs.terms = 'You must accept the terms';
    }
    if (step === 2) {
      if (!vehicleDetails?.make?.trim()) errs.make = 'Required';
      if (!vehicleDetails?.model?.trim()) errs.model = 'Required';
      if (!vehicleDetails?.location?.trim()) errs.location = 'Required';
    }
    if (step === 5 && !isEditMode) {
      if (photos?.length < 6) errs.photos = 'Minimum 6 photos required';
    }
    if (step === 6) {
      if (!pricing?.baseDailyRate) errs.baseDailyRate = 'Required';
      if (!pricing?.depositAmount) errs.depositAmount = 'Required';
    }
    setErrors(errs);
    return Object.keys(errs)?.length === 0;
  };

  const handleNext = () => {
    if (validateStep()) setStep(s => Math.min(7, s + 1));
  };

  const handlePhotoUpload = (e) => {
    const files = Array.from(e?.target?.files || []);
    setPhotos(prev => [...prev, ...files]?.slice(0, 12));
  };

  const handleSubmit = async () => {
    if (!user) { navigate('/customer-login-screen'); return; }

    // Enforce minimum photo requirement at submit time (new listings only)
    if (!isEditMode && photos?.length < 6) {
      setErrors({ submit: `Please upload at least 6 photos before submitting. You have ${photos?.length} photo${photos?.length === 1 ? '' : 's'}.` });
      setStep(5);
      return;
    }

    setSubmitting(true);
    setErrors({});

    // Helper: derive file extension from MIME type
    const getExtFromMime = (mimeType) => {
      const map = { 'image/jpeg': 'jpg', 'image/jpg': 'jpg', 'image/png': 'png', 'image/webp': 'webp', 'image/gif': 'gif' };
      return map?.[mimeType] || 'jpg';
    };

    // Helper: upload a single photo and insert vehicle_media row
    const uploadPhoto = async (photo, vehicleId, index, sortOffset = 0) => {
      const mimeType = photo?.type || 'image/jpeg';
      const ext = getExtFromMime(mimeType);
      const path = `vehicles/${vehicleId}/${Date.now()}_${index}.${ext}`;

      const { data: uploadData, error: uploadError } = await supabase?.storage
        ?.from('vehicle-media')
        ?.upload(path, photo, { contentType: mimeType, upsert: false });

      if (uploadError) {
        throw new Error(`Photo ${index + 1} (${photo?.name || 'unnamed'}) upload failed: ${uploadError?.message}`);
      }

      const storagePath = uploadData?.path;
      if (!storagePath) {
        throw new Error(`Photo ${index + 1} uploaded but no path was returned from storage.`);
      }

      const { error: mediaInsertError } = await supabase?.from('vehicle_media')?.insert({
        vehicle_id: vehicleId,
        storage_path: storagePath,
        sort_order: sortOffset + index,
        is_primary: sortOffset === 0 && index === 0,
        media_type: 'photo',
      });

      if (mediaInsertError) {
        throw new Error(`Photo ${index + 1} uploaded but vehicle_media insert failed: ${mediaInsertError?.message}`);
      }
    };

    try {
      const vehiclePayload = {
        title: `${vehicleDetails?.make} ${vehicleDetails?.model} ${vehicleDetails?.year}`,
        make: vehicleDetails?.make,
        model: vehicleDetails?.model,
        year: vehicleDetails?.year,
        vehicle_type: 'sedan',
        seats: vehicleDetails?.seats,
        transmission: vehicleDetails?.transmission,
        fuel: vehicleDetails?.fuel,
        color: vehicleDetails?.color,
        license_plate: vehicleDetails?.plate,
        location_address: vehicleDetails?.location,
        rental_mode: vehicleDetails?.rentalMode,
        description: vehicleDetails?.description,
        min_rental_days: availability?.minDays,
        max_rental_days: availability?.maxDays,
        min_driver_age: availability?.minDriverAge,
        deposit_required: availability?.depositRequired,
      };

      let vehicleId = editVehicleId;

      if (isEditMode) {
        // UPDATE existing vehicle
        const { error: updateError } = await supabase
          ?.from('vehicles')
          ?.update(vehiclePayload)
          ?.eq('id', editVehicleId)
          ?.eq('owner_id', user?.id);

        if (updateError) {
          throw new Error(`Failed to update vehicle: ${updateError?.message}`);
        }

        // Update pricing
        const { data: existingPricing } = await supabase?.from('vehicle_pricing')?.select('id')?.eq('vehicle_id', editVehicleId)?.single();
        const pricingPayload = {
          vehicle_id: editVehicleId,
          base_daily_rate: parseFloat(pricing?.baseDailyRate),
          weekend_multiplier: parseFloat(pricing?.weekendMultiplier),
          chauffeur_daily_addon: parseFloat(pricing?.chauffeurAddon || 0),
          deposit_amount: parseFloat(pricing?.depositAmount),
          min_days: availability?.minDays,
          mileage_limit_km_per_day: pricing?.mileageLimit,
          overage_fee_per_km: pricing?.overageFee,
        };

        if (existingPricing?.id) {
          const { error: pricingUpdateError } = await supabase?.from('vehicle_pricing')?.update(pricingPayload)?.eq('id', existingPricing?.id);
          if (pricingUpdateError) throw new Error(`Pricing update failed: ${pricingUpdateError?.message}`);
        } else {
          const { error: pricingInsertError } = await supabase?.from('vehicle_pricing')?.insert(pricingPayload);
          if (pricingInsertError) throw new Error(`Pricing save failed: ${pricingInsertError?.message}`);
        }

        // Upload any new photos added during edit
        if (photos?.length > 0) {
          const { data: existingMedia } = await supabase?.from('vehicle_media')?.select('id')?.eq('vehicle_id', editVehicleId);
          const startOrder = existingMedia?.length || 0;
          const uploadFailures = [];

          for (let i = 0; i < photos?.length; i++) {
            try {
              await uploadPhoto(photos?.[i], editVehicleId, i, startOrder);
            } catch (photoErr) {
              console.error('[ListMyCar] Edit photo upload error:', photoErr);
              uploadFailures?.push(photoErr?.message);
            }
          }

          if (uploadFailures?.length > 0) {
            setErrors({ uploadWarning: `Details saved, but ${uploadFailures?.length} photo${uploadFailures?.length === 1 ? '' : 's'} failed to upload:\n• ${uploadFailures?.join('\n• ')}` });
          }
        }

        setShowSuccess(true);
        setTimeout(() => navigate('/my-vehicles'), 2500);
        return;
      }

      // NEW listing flow (unchanged)
      const { data: vehicleData, error: vehicleError } = await supabase?.from('vehicles')?.insert({
        owner_id: user?.id,
        ...vehiclePayload,
        status: 'pending_review',
      })?.select()?.single();

      if (vehicleError) {
        throw new Error(`Failed to create vehicle listing: ${vehicleError?.message}`);
      }
      if (!vehicleData?.id) {
        throw new Error('Vehicle record was created but no ID was returned. Please try again.');
      }

      vehicleId = vehicleData?.id;

      // Upload photos — fail hard on first error so the issue is visible
      const uploadFailures = [];
      for (let i = 0; i < photos?.length; i++) {
        try {
          await uploadPhoto(photos?.[i], vehicleId, i, 0);
        } catch (photoErr) {
          console.error(`[ListMyCar] Photo ${i + 1} upload error:`, photoErr);
          uploadFailures?.push(photoErr?.message);
        }
      }

      // Create pricing — hard fail if this errors
      const { error: pricingError } = await supabase?.from('vehicle_pricing')?.insert({
        vehicle_id: vehicleId,
        base_daily_rate: parseFloat(pricing?.baseDailyRate),
        weekend_multiplier: parseFloat(pricing?.weekendMultiplier),
        chauffeur_daily_addon: parseFloat(pricing?.chauffeurAddon || 0),
        deposit_amount: parseFloat(pricing?.depositAmount),
        min_days: availability?.minDays,
        mileage_limit_km_per_day: pricing?.mileageLimit,
        overage_fee_per_km: pricing?.overageFee,
      });

      if (pricingError) {
        throw new Error(`Vehicle was saved but pricing could not be set: ${pricingError?.message}. Please contact support with your vehicle ID: ${vehicleId}`);
      }

      // Surface any partial upload failures as a warning (listing still created)
      if (uploadFailures?.length > 0) {
        setErrors({
          uploadWarning: `Your listing was submitted, but ${uploadFailures?.length} photo${uploadFailures?.length === 1 ? '' : 's'} failed to upload:\n• ${uploadFailures?.join('\n• ')}\n\nYou can add more photos from My Vehicles.`,
        });
        setShowSuccess(true);
        setTimeout(() => navigate('/my-vehicles'), 5000);
        return;
      }

      setShowSuccess(true);
      setTimeout(() => navigate('/my-vehicles'), 2500);
    } catch (e) {
      console.error('[ListMyCar] Submission error:', e);
      setErrors({ submit: e?.message || 'Submission failed. Please try again.' });
    } finally {
      setSubmitting(false);
    }
  };

  const inputClass = (err) => `w-full px-4 py-3 rounded-xl text-white placeholder-white/30 text-sm outline-none focus:ring-2 focus:ring-[#ffd700]/40 ${err ? 'ring-1 ring-red-400/50' : ''}`;
  const inputStyle = { background: 'rgba(255,255,255,0.07)', border: '1px solid rgba(255,255,255,0.12)' };
  const labelClass = "text-white/60 text-xs font-semibold uppercase tracking-wider mb-2 block";

  if (loadingEdit) {
    return (
      <div className="min-h-screen flex items-center justify-center" style={{ background: '#0a1628' }}>
        <SpinnerOverlay message="Loading listing details..." />
      </div>
    );
  }

  return (
    <div className="min-h-screen" style={{ background: '#0a1628', fontFamily: "'DM Sans', sans-serif" }}>
      <style>{`@import url('https://fonts.googleapis.com/css2?family=DM+Sans:wght@400;500;600;700;800;900&display=swap');`}</style>
      {submitting && <SpinnerOverlay message={isEditMode ? 'Saving changes...' : 'Submitting your listing...'} />}
      {showSuccess && <SuccessToast message={isEditMode ? 'Listing updated successfully!' : 'Listing submitted for review! Redirecting...'} />}
      {/* Header */}
      <div className="sticky top-0 z-50 border-b border-white/10" style={{ background: 'rgba(10,22,40,0.95)', backdropFilter: 'blur(20px)' }}>
        <div className="max-w-3xl mx-auto px-4 py-4 flex items-center gap-3">
          <button onClick={() => step > 1 ? setStep(s => s - 1) : navigate(isEditMode ? '/my-vehicles' : '/')} className="p-2 rounded-xl hover:bg-white/10 transition-colors">
            <Icon name="ArrowLeft" size={20} className="text-white" />
          </button>
          <div className="flex-1">
            <h1 className="text-white font-bold text-base">{isEditMode ? 'Edit Listing' : 'List Your Car'}</h1>
            <p className="text-white/40 text-xs">Step {step} of {STEPS?.length}: {STEPS?.[step - 1]?.label}</p>
          </div>
          {isEditMode && (
            <span className="px-2 py-1 rounded-lg text-xs font-bold" style={{ background: 'rgba(96,165,250,0.15)', color: '#60a5fa' }}>
              Editing
            </span>
          )}
        </div>
        {/* Progress Bar */}
        <div className="h-1 bg-white/10">
          <div className="h-full bg-[#ffd700] transition-all duration-500" style={{ width: `${(step / STEPS?.length) * 100}%` }} />
        </div>
      </div>
      <div className="max-w-3xl mx-auto px-4 sm:px-6 py-8">
        {/* Step Indicators */}
        <div className="flex items-center justify-between mb-8 overflow-x-auto pb-2">
          {STEPS?.map((s, i) => (
            <div key={s?.id} className="flex items-center">
              <div className={`flex flex-col items-center gap-1 ${i < STEPS?.length - 1 ? 'mr-2' : ''}`}>
                <div className={`w-8 h-8 rounded-full flex items-center justify-center transition-all ${step > s?.id ? 'bg-[#ffd700]' : step === s?.id ? 'bg-[#ffd700]/20 border-2 border-[#ffd700]' : 'bg-white/10'}`}>
                  {step > s?.id ? <Icon name="Check" size={14} className="text-[#0a1628]" /> : <Icon name={s?.icon} size={14} className={step === s?.id ? 'text-[#ffd700]' : 'text-white/30'} />}
                </div>
                <span className={`text-[10px] font-semibold hidden sm:block ${step === s?.id ? 'text-[#ffd700]' : 'text-white/30'}`}>{s?.label}</span>
              </div>
              {i < STEPS?.length - 1 && <div className={`w-6 h-px mx-1 ${step > s?.id ? 'bg-[#ffd700]' : 'bg-white/15'}`} />}
            </div>
          ))}
        </div>

        <div className="rounded-2xl p-6 space-y-5" style={{ background: 'rgba(255,255,255,0.04)', border: '1px solid rgba(255,255,255,0.08)' }}>
          {/* Step 1: KYC */}
          {step === 1 && (
            <div className="space-y-4">
              <h2 className="text-white font-bold text-xl">Owner Verification</h2>
              <p className="text-white/40 text-sm">We need to verify your identity before you can list vehicles.</p>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div><label className={labelClass}>Full Name *</label><input className={inputClass(errors?.fullName)} style={inputStyle} placeholder="John Kamau" value={kyc?.fullName} onChange={e => setKyc(k => ({ ...k, fullName: e?.target?.value }))} />{errors?.fullName && <p className="text-red-400 text-xs mt-1">{errors?.fullName}</p>}</div>
                <div><label className={labelClass}>Phone Number *</label><input className={inputClass(errors?.phone)} style={inputStyle} placeholder="+254 7XX XXX XXX" value={kyc?.phone} onChange={e => setKyc(k => ({ ...k, phone: e?.target?.value }))} />{errors?.phone && <p className="text-red-400 text-xs mt-1">{errors?.phone}</p>}</div>
                <div><label className={labelClass}>National ID / Passport</label><input className={inputClass()} style={inputStyle} placeholder="ID Number" value={kyc?.idNumber} onChange={e => setKyc(k => ({ ...k, idNumber: e?.target?.value }))} /></div>
                <div>
                  <label className={labelClass}>Payout Method</label>
                  <select className={inputClass()} style={{ ...inputStyle, colorScheme: 'dark' }} value={kyc?.payoutMethod} onChange={e => setKyc(k => ({ ...k, payoutMethod: e?.target?.value }))}>
                    <option value="wallet">OnTheGo Wallet</option>
                    <option value="mpesa">M-Pesa</option>
                    <option value="bank">Bank Transfer</option>
                  </select>
                </div>
              </div>
              <label className="flex items-start gap-3 cursor-pointer">
                <input type="checkbox" checked={kyc?.termsAccepted} onChange={e => setKyc(k => ({ ...k, termsAccepted: e?.target?.checked }))} className="mt-0.5 w-4 h-4 accent-[#ffd700]" />
                <span className="text-white/60 text-sm">I agree to the OnTheGo Host Terms, commission structure (10% platform fee), and vehicle listing guidelines.</span>
              </label>
              {errors?.terms && <p className="text-red-400 text-xs">{errors?.terms}</p>}
            </div>
          )}

          {/* Step 2: Vehicle Details */}
          {step === 2 && (
            <div className="space-y-4">
              <h2 className="text-white font-bold text-xl">Vehicle Details</h2>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div><label className={labelClass}>Make *</label><input className={inputClass(errors?.make)} style={inputStyle} placeholder="Toyota" value={vehicleDetails?.make} onChange={e => setVehicleDetails(v => ({ ...v, make: e?.target?.value }))} />{errors?.make && <p className="text-red-400 text-xs mt-1">{errors?.make}</p>}</div>
                <div><label className={labelClass}>Model *</label><input className={inputClass(errors?.model)} style={inputStyle} placeholder="Prado" value={vehicleDetails?.model} onChange={e => setVehicleDetails(v => ({ ...v, model: e?.target?.value }))} />{errors?.model && <p className="text-red-400 text-xs mt-1">{errors?.model}</p>}</div>
                <div><label className={labelClass}>Year</label><input type="number" className={inputClass()} style={inputStyle} value={vehicleDetails?.year} onChange={e => setVehicleDetails(v => ({ ...v, year: parseInt(e?.target?.value) }))} /></div>
                <div><label className={labelClass}>Color</label><input className={inputClass()} style={inputStyle} placeholder="White" value={vehicleDetails?.color} onChange={e => setVehicleDetails(v => ({ ...v, color: e?.target?.value }))} /></div>
                <div><label className={labelClass}>Seats</label><input type="number" min={2} max={15} className={inputClass()} style={inputStyle} value={vehicleDetails?.seats} onChange={e => setVehicleDetails(v => ({ ...v, seats: parseInt(e?.target?.value) }))} /></div>
                <div>
                  <label className={labelClass}>Transmission</label>
                  <select className={inputClass()} style={{ ...inputStyle, colorScheme: 'dark' }} value={vehicleDetails?.transmission} onChange={e => setVehicleDetails(v => ({ ...v, transmission: e?.target?.value }))}>
                    <option value="automatic">Automatic</option>
                    <option value="manual">Manual</option>
                  </select>
                </div>
                <div>
                  <label className={labelClass}>Fuel Type</label>
                  <select className={inputClass()} style={{ ...inputStyle, colorScheme: 'dark' }} value={vehicleDetails?.fuel} onChange={e => setVehicleDetails(v => ({ ...v, fuel: e?.target?.value }))}>
                    <option value="petrol">Petrol</option>
                    <option value="diesel">Diesel</option>
                    <option value="hybrid">Hybrid</option>
                    <option value="electric">Electric</option>
                  </select>
                </div>
                <div>
                  <label className={labelClass}>Rental Mode</label>
                  <select className={inputClass()} style={{ ...inputStyle, colorScheme: 'dark' }} value={vehicleDetails?.rentalMode} onChange={e => setVehicleDetails(v => ({ ...v, rentalMode: e?.target?.value }))}>
                    <option value="self_drive">Self-Drive Only</option>
                    <option value="chauffeur">Chauffeur Only</option>
                    <option value="both">Both Options</option>
                  </select>
                </div>
              </div>
              <div><label className={labelClass}>Pickup Location *</label><input className={inputClass(errors?.location)} style={inputStyle} placeholder="Westlands, Nairobi" value={vehicleDetails?.location} onChange={e => setVehicleDetails(v => ({ ...v, location: e?.target?.value }))} />{errors?.location && <p className="text-red-400 text-xs mt-1">{errors?.location}</p>}</div>
              <div><label className={labelClass}>Description</label><textarea rows={3} className={inputClass()} style={inputStyle} placeholder="Tell renters about your vehicle..." value={vehicleDetails?.description} onChange={e => setVehicleDetails(v => ({ ...v, description: e?.target?.value }))} /></div>
            </div>
          )}

          {/* Step 3: Availability */}
          {step === 3 && (
            <div className="space-y-4">
              <h2 className="text-white font-bold text-xl">Availability Rules</h2>
              <div className="grid grid-cols-2 gap-4">
                <div><label className={labelClass}>Min Rental Days</label><input type="number" min={1} className={inputClass()} style={inputStyle} value={availability?.minDays} onChange={e => setAvailability(a => ({ ...a, minDays: parseInt(e?.target?.value) }))} /></div>
                <div><label className={labelClass}>Max Rental Days</label><input type="number" min={1} className={inputClass()} style={inputStyle} value={availability?.maxDays} onChange={e => setAvailability(a => ({ ...a, maxDays: parseInt(e?.target?.value) }))} /></div>
                <div><label className={labelClass}>Min Driver Age</label><input type="number" min={18} className={inputClass()} style={inputStyle} value={availability?.minDriverAge} onChange={e => setAvailability(a => ({ ...a, minDriverAge: parseInt(e?.target?.value) }))} /></div>
              </div>
              <label className="flex items-center gap-3 cursor-pointer p-4 rounded-xl" style={{ border: '1px solid rgba(255,255,255,0.1)' }}>
                <input type="checkbox" checked={availability?.depositRequired} onChange={e => setAvailability(a => ({ ...a, depositRequired: e?.target?.checked }))} className="w-4 h-4 accent-[#ffd700]" />
                <div>
                  <div className="text-white font-semibold text-sm">Require Security Deposit</div>
                  <div className="text-white/40 text-xs">Deposit is held and released after successful return</div>
                </div>
              </label>
            </div>
          )}

          {/* Step 4: Documents */}
          {step === 4 && (
            <div className="space-y-4">
              <h2 className="text-white font-bold text-xl">Vehicle Documents</h2>
              <p className="text-white/40 text-sm">Upload required documents for verification. These are kept private.</p>
              {[
                { key: 'logbook', label: 'Logbook / Registration', desc: 'Vehicle registration certificate' },
                { key: 'insurance', label: 'Insurance Certificate', desc: 'Valid comprehensive insurance' },
              ]?.map(doc => (
                <label key={doc?.key} className="flex items-center justify-between p-4 rounded-xl cursor-pointer hover:bg-white/5 transition-colors" style={{ border: '2px dashed rgba(255,255,255,0.15)' }}>
                  <div className="flex items-center gap-3">
                    <Icon name="FileText" size={20} className="text-[#ffd700]" />
                    <div>
                      <div className="text-white font-semibold text-sm">{doc?.label}</div>
                      <div className="text-white/40 text-xs">{docFiles?.[doc?.key] ? docFiles?.[doc?.key]?.name : doc?.desc}</div>
                    </div>
                  </div>
                  {docFiles?.[doc?.key] ? <Icon name="CheckCircle" size={20} className="text-[#34d399]" /> : <Icon name="Upload" size={20} className="text-white/30" />}
                  <input type="file" accept="image/*,.pdf" className="hidden" onChange={e => setDocFiles(d => ({ ...d, [doc?.key]: e?.target?.files?.[0] }))} />
                </label>
              ))}
            </div>
          )}

          {/* Step 5: Photos */}
          {step === 5 && (
            <div className="space-y-4">
              <h2 className="text-white font-bold text-xl">Vehicle Photos</h2>
              <p className="text-white/40 text-sm">
                {isEditMode
                  ? 'Add more photos to your listing. Existing photos are preserved.' :'Upload at least 6 photos: front, rear, sides, interior, odometer, and tires.'}
              </p>
              {isEditMode && (
                <div className="rounded-xl p-3 flex items-center gap-2" style={{ background: 'rgba(96,165,250,0.08)', border: '1px solid rgba(96,165,250,0.2)' }}>
                  <Icon name="Info" size={14} className="text-[#60a5fa] flex-shrink-0" />
                  <p className="text-[#60a5fa] text-xs">New photos selected here will be added to your existing photos.</p>
                </div>
              )}
              <div className="grid grid-cols-3 sm:grid-cols-4 gap-3">
                {photos?.map((photo, i) => (
                  <div key={i} className="relative aspect-square rounded-xl overflow-hidden bg-white/10">
                    <img src={URL.createObjectURL(photo)} alt={`Vehicle photo ${i + 1}`} className="w-full h-full object-cover" />
                    <button onClick={() => setPhotos(p => p?.filter((_, idx) => idx !== i))} className="absolute top-1 right-1 w-5 h-5 rounded-full bg-red-500 flex items-center justify-center">
                      <Icon name="X" size={10} className="text-white" />
                    </button>
                  </div>
                ))}
                {photos?.length < 12 && (
                  <label className="aspect-square rounded-xl flex flex-col items-center justify-center cursor-pointer hover:bg-white/10 transition-colors" style={{ border: '2px dashed rgba(255,255,255,0.2)' }}>
                    <Icon name="Plus" size={24} className="text-white/40 mb-1" />
                    <span className="text-white/30 text-xs">Add Photo</span>
                    <input type="file" accept="image/*" multiple className="hidden" onChange={handlePhotoUpload} />
                  </label>
                )}
              </div>
              {!isEditMode && (
                <div className="flex items-center gap-2">
                  <div className={`w-2 h-2 rounded-full ${photos?.length >= 6 ? 'bg-[#34d399]' : 'bg-red-400'}`} />
                  <span className={`text-sm font-semibold ${photos?.length >= 6 ? 'text-[#34d399]' : 'text-red-400'}`}>{photos?.length}/6 minimum photos uploaded</span>
                </div>
              )}
              {errors?.photos && <p className="text-red-400 text-xs">{errors?.photos}</p>}
            </div>
          )}

          {/* Step 6: Pricing */}
          {step === 6 && (
            <div className="space-y-4">
              <h2 className="text-white font-bold text-xl">Pricing Setup</h2>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div><label className={labelClass}>Base Daily Rate (KES) *</label><input type="number" className={inputClass(errors?.baseDailyRate)} style={inputStyle} placeholder="5000" value={pricing?.baseDailyRate} onChange={e => setPricing(p => ({ ...p, baseDailyRate: e?.target?.value }))} />{errors?.baseDailyRate && <p className="text-red-400 text-xs mt-1">{errors?.baseDailyRate}</p>}</div>
                <div><label className={labelClass}>Weekend Multiplier</label><input type="number" step="0.1" min="1" className={inputClass()} style={inputStyle} value={pricing?.weekendMultiplier} onChange={e => setPricing(p => ({ ...p, weekendMultiplier: parseFloat(e?.target?.value) }))} /></div>
                <div><label className={labelClass}>Chauffeur Add-on (KES/day)</label><input type="number" className={inputClass()} style={inputStyle} placeholder="2500" value={pricing?.chauffeurAddon} onChange={e => setPricing(p => ({ ...p, chauffeurAddon: e?.target?.value }))} /></div>
                <div><label className={labelClass}>Security Deposit (KES) *</label><input type="number" className={inputClass(errors?.depositAmount)} style={inputStyle} placeholder="15000" value={pricing?.depositAmount} onChange={e => setPricing(p => ({ ...p, depositAmount: e?.target?.value }))} />{errors?.depositAmount && <p className="text-red-400 text-xs mt-1">{errors?.depositAmount}</p>}</div>
                <div><label className={labelClass}>Daily Mileage Limit (km)</label><input type="number" className={inputClass()} style={inputStyle} value={pricing?.mileageLimit} onChange={e => setPricing(p => ({ ...p, mileageLimit: parseInt(e?.target?.value) }))} /></div>
                <div><label className={labelClass}>Overage Fee (KES/km)</label><input type="number" className={inputClass()} style={inputStyle} value={pricing?.overageFee} onChange={e => setPricing(p => ({ ...p, overageFee: parseFloat(e?.target?.value) }))} /></div>
              </div>
              {pricing?.baseDailyRate && (
                <div className="rounded-xl p-4" style={{ background: 'rgba(255,215,0,0.06)', border: '1px solid rgba(255,215,0,0.15)' }}>
                  <p className="text-[#ffd700] text-sm font-semibold">💰 Estimated Monthly Earnings</p>
                  <p className="text-white/60 text-xs mt-1">At 20 days/month: KES {(parseFloat(pricing?.baseDailyRate || 0) * 20 * 0.9)?.toLocaleString()} (after 10% platform fee)</p>
                </div>
              )}
            </div>
          )}

          {/* Step 7: Review */}
          {step === 7 && (
            <div className="space-y-4">
              <h2 className="text-white font-bold text-xl">{isEditMode ? 'Review Changes' : 'Review & Submit'}</h2>
              <p className="text-white/40 text-sm">
                {isEditMode
                  ? 'Review your updated details before saving.'
                  : 'Your listing will be reviewed by our team within 24 hours before going live.'}
              </p>
              <div className="space-y-3">
                {[
                  { label: 'Vehicle', value: `${vehicleDetails?.make} ${vehicleDetails?.model} ${vehicleDetails?.year}` },
                  { label: 'Location', value: vehicleDetails?.location },
                  { label: 'Rental Mode', value: vehicleDetails?.rentalMode?.replace('_', ' ') },
                  { label: 'Daily Rate', value: `KES ${parseFloat(pricing?.baseDailyRate || 0)?.toLocaleString()}` },
                  { label: 'Deposit', value: `KES ${parseFloat(pricing?.depositAmount || 0)?.toLocaleString()}` },
                  { label: 'New Photos', value: photos?.length > 0 ? `${photos?.length} to add` : isEditMode ? 'No new photos' : '0 uploaded' },
                ]?.map(item => (
                  <div key={item?.label} className="flex justify-between p-3 rounded-xl" style={{ background: 'rgba(255,255,255,0.04)' }}>
                    <span className="text-white/50 text-sm">{item?.label}</span>
                    <span className="text-white font-semibold text-sm capitalize">{item?.value}</span>
                  </div>
                ))}
              </div>
              {!isEditMode && (
                <div className="rounded-xl p-4" style={{ background: 'rgba(255,215,0,0.06)', border: '1px solid rgba(255,215,0,0.15)' }}>
                  <p className="text-[#ffd700] text-sm font-semibold">📋 What happens next?</p>
                  <ul className="text-white/50 text-xs mt-2 space-y-1">
                    <li>• Admin reviews your listing (24-48 hours)</li>
                    <li>• You receive notification when approved</li>
                    <li>• Listing goes live and customers can book</li>
                  </ul>
                </div>
              )}
              {errors?.submit && (
                <div className="p-3 rounded-xl" style={{ background: 'rgba(239,68,68,0.1)', border: '1px solid rgba(239,68,68,0.3)' }}>
                  <p className="text-red-400 text-sm whitespace-pre-line">{errors?.submit}</p>
                </div>
              )}
              {errors?.uploadWarning && (
                <div className="p-3 rounded-xl" style={{ background: 'rgba(251,191,36,0.08)', border: '1px solid rgba(251,191,36,0.3)' }}>
                  <p className="text-yellow-400 text-sm font-semibold mb-1">⚠️ Partial Upload Warning</p>
                  <p className="text-yellow-300/80 text-xs whitespace-pre-line">{errors?.uploadWarning}</p>
                </div>
              )}
            </div>
          )}
        </div>

        {/* Navigation Buttons */}
        <div className="flex gap-3 mt-6">
          {step > 1 && (
            <button onClick={() => setStep(s => s - 1)} className="btn-secondary flex-1">
              Back
            </button>
          )}
          {step < 7 ? (
            <button onClick={handleNext} className="btn-primary flex-1">
              Continue <Icon name="ArrowRight" size={16} />
            </button>
          ) : (
            <button onClick={handleSubmit} disabled={submitting} className="btn-primary flex-1">
              {submitting ? (isEditMode ? 'Saving...' : 'Submitting...') : (isEditMode ? 'Save Changes' : 'Submit for Review')}
            </button>
          )}
        </div>
      </div>
    </div>
  );
};

export default ListMyCarScreen;
